import { getCurrencySymbol } from "shared/money/balances";
import type { BudgetTransaction } from "@/types/BudgetTransaction";
import type { CurrencyBalances } from "@/types/Balance";

export function createBudgetTransactionId(): string {
    const randomId = globalThis.crypto?.randomUUID?.();
    return randomId || `budget-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function parseTxDate(tx: BudgetTransaction): Date {
    const raw = tx.date;
    const asNum = Number(raw);
    if (!isNaN(asNum) && asNum > 1e12) return new Date(asNum);
    return new Date(raw);
}

export function formatLocaleAmount(amount: number, currency: string): string {
    const abs = Math.abs(amount);
    const formatted = new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(abs);
    return `${getCurrencySymbol(currency)}${formatted}`;
}

export function getBudgetTransactionId(tx: BudgetTransaction): string {
    return String(tx?._id ?? "").trim();
}

export function getBudgetTransactionSignature(tx: BudgetTransaction): string {
    const dateTime = parseTxDate(tx).getTime();
    const datePart = Number.isFinite(dateTime) ? String(dateTime) : String(tx.date ?? "");
    return [
        String(tx.name ?? "").trim().toLowerCase(),
        String(Number(tx.amount) || 0),
        String(tx.currency || "ARS").trim().toUpperCase(),
        String(tx.type || "expense"),
        String(tx.category || ""),
        datePart,
        String(tx.liquidityPoolId || ""),
    ].join("|");
}

export function getBudgetTransactionIdentity(tx: BudgetTransaction): string {
    return getBudgetTransactionId(tx) || `legacy-${getBudgetTransactionSignature(tx)}`;
}

export function getBudgetTransactionKey(tx: BudgetTransaction, fallbackIndex = 0): string {
    return `${getBudgetTransactionIdentity(tx)}-${fallbackIndex}`;
}

/**
 * Keeps one row per persisted `_id`.
 * Legacy optimistic drafts sometimes have no id; those are deduped by content and
 * dropped when the persisted server row with the same content is present.
 */
export function dedupeBudgetTransactionsById(transactions: BudgetTransaction[]): BudgetTransaction[] {
    const persistedSignatures = new Set(
        transactions
            .filter((tx) => getBudgetTransactionId(tx))
            .map(getBudgetTransactionSignature),
    );
    const seen = new Set<string>();
    const seenLegacy = new Set<string>();
    const out: BudgetTransaction[] = [];
    for (const tx of transactions) {
        const id = getBudgetTransactionId(tx);
        if (id) {
            if (seen.has(id)) continue;
            seen.add(id);
            out.push({ ...tx, _id: id });
            continue;
        }

        const signature = getBudgetTransactionSignature(tx);
        if (persistedSignatures.has(signature) || seenLegacy.has(signature)) {
            continue;
        }
        seenLegacy.add(signature);
        out.push(tx);
    }
    return out;
}

export type CurrencyTotals = Record<string, { spent: number; received: number }>;

/** Card statement payment rows move wallet cash but should not inflate budget spending. */
export function isStatementPaymentTransaction(tx: BudgetTransaction): boolean {
    return Boolean(tx.invoiceImport?.isStatementPayment);
}

/** Transactions that affect monthly budget spent/received totals. */
export function isBudgetImpactingTransaction(tx: BudgetTransaction): boolean {
    return !isStatementPaymentTransaction(tx);
}

/** Imported card purchase line (budget-only; wallet links on statement payment). */
export function isCardPurchaseImportTransaction(tx: BudgetTransaction): boolean {
    return Boolean(tx.invoiceImport?.importId) && !isStatementPaymentTransaction(tx);
}

export function resolveLiquidityPoolIdForSave(
    tx: BudgetTransaction,
    editExecute: boolean,
    editLiquidityPoolId: string,
): string | undefined {
    if (isCardPurchaseImportTransaction(tx)) return undefined;
    return editExecute ? editLiquidityPoolId || undefined : undefined;
}

/** month1Based: 1 = January */
export function totalsForMonth(
    txs: BudgetTransaction[],
    year: number,
    month1Based: number,
): CurrencyTotals {
    const totals: CurrencyTotals = {};
    txs.forEach((tx) => {
        if (!isBudgetImpactingTransaction(tx)) return;
        const d = parseTxDate(tx);
        if (isNaN(d.getTime())) return;
        if (d.getMonth() + 1 !== month1Based || d.getFullYear() !== year) return;
        const cur = tx.currency || "ARS";
        if (!totals[cur]) totals[cur] = { spent: 0, received: 0 };
        const isIncome = (tx.type ?? "expense") === "income";
        if (isIncome) totals[cur].received += tx.amount;
        else totals[cur].spent += tx.amount;
    });
    return totals;
}

/** Net signed amount per currency: income minus expenses (spent/received convention from totals). */
export function netBalancesFromBudgetTotals(totals: CurrencyTotals): CurrencyBalances {
    const byCur: CurrencyBalances = {};
    for (const [cur, { spent, received }] of Object.entries(totals)) {
        byCur[cur] = received - spent;
    }
    return byCur;
}

/** Signed net per currency for a list of budget transactions (e.g. one calendar day). */
export function netByCurrencyFromTransactions(txs: BudgetTransaction[]): CurrencyBalances {
    const byCur: CurrencyBalances = {};
    for (const tx of txs) {
        if (!isBudgetImpactingTransaction(tx)) continue;
        const cur = tx.currency || "ARS";
        const sign = (tx.type ?? "expense") === "income" ? 1 : -1;
        byCur[cur] = (byCur[cur] ?? 0) + sign * tx.amount;
    }
    return byCur;
}
